import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Calendar, Mail } from "lucide-react";
import SectionEyebrow from "@/components/site/SectionEyebrow";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACUITY = "/book";

const inquiryTypes = [
    { value: "speaking", label: "Speaking / Keynote" },
    { value: "training", label: "Faculty / Staff Training" },
    { value: "workshop", label: "Workshop" },
    { value: "curriculum", label: "9-Module Curriculum" },
    { value: "keynote", label: "Custom Partnership" },
    { value: "general", label: "General Inquiry" },
];

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        organization: "",
        role: "",
        inquiry_type: "general",
        message: "",
    });
    const [loading, setLoading] = useState(false);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await axios.post(`${API}/contact`, form);
            toast.success("Thank you. Shana will be in touch shortly.");
            setForm({
                name: "",
                email: "",
                organization: "",
                role: "",
                inquiry_type: "general",
                message: "",
            });
        } catch (err) {
            const msg = err?.response?.data?.detail || "Submission failed. Please try again.";
            toast.error(typeof msg === "string" ? msg : "Submission failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const inputClass =
        "w-full font-sans text-base bg-transparent border-b border-[#0c0f3d]/25 py-4 outline-none focus:border-[#e47a6a] transition-colors text-[#0c0f3d] placeholder:text-[#0c0f3d]/40";

    return (
        <main className="bg-[#fbf9f8]" data-testid="contact-page">
            {/* HERO */}
            <section className="pt-36 pb-20 md:pt-44 md:pb-24">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
                        <div className="lg:col-span-8">
                            <SectionEyebrow>Contact</SectionEyebrow>
                            <h1 className="font-serif text-5xl md:text-7xl lg:text-[96px] leading-[0.92] tracking-tight text-[#0c0f3d]">
                                Let&apos;s build something
                                <span className="block italic text-[#e47a6a]">connection-centered.</span>
                            </h1>
                        </div>
                        <div className="lg:col-span-4">
                            <p className="font-sans text-base md:text-lg text-[#0c0f3d]/75 leading-relaxed">
                                Tell us about your institution, your team, or your event. Shana will personally respond to begin architecting the right partnership.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* TWO COLUMN: BOOKING + FORM */}
            <section className="pb-24 md:pb-36">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
                    {/* BOOKING SIDEBAR */}
                    <aside className="lg:col-span-4 space-y-10">
                        <div className="bg-[#0c0f3d] text-[#fbf9f8] p-10">
                            <Calendar size={28} className="text-[#ffb5ab] mb-5" />
                            <h3 className="font-serif text-3xl md:text-4xl leading-tight mb-4">
                                Book a strategy call
                            </h3>
                            <p className="font-sans text-sm text-[#e3c7b9]/80 leading-relaxed mb-7">
                                Choose the call type that fits your organization &mdash; educational, corporate/nonprofit, or speaking engagement.
                            </p>
                            <Link
                                to={ACUITY}
                                data-testid="contact-book-call-btn"
                                className="group inline-flex items-center gap-3 px-6 py-3 bg-[#ffb5ab] text-[#0c0f3d] text-xs uppercase tracking-[0.2em] hover:bg-[#fbf9f8] transition-colors duration-300"
                            >
                                Schedule Now
                                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                            </Link>
                        </div>

                        <div className="bg-[#e3c7b9]/40 p-10">
                            <Mail size={24} className="text-[#e47a6a] mb-5" />
                            <p className="font-sans text-xs uppercase tracking-[0.25em] text-[#6d91ad] mb-2">
                                Inquiries
                            </p>
                            <p className="font-serif text-2xl text-[#0c0f3d] leading-tight">
                                Use the form to reach Shana directly. Most replies arrive within 2 business days.
                            </p>
                        </div>

                        <div>
                            <p className="font-sans text-xs uppercase tracking-[0.25em] text-[#6d91ad] mb-3">
                                Best for
                            </p>
                            <ul className="font-serif text-xl text-[#0c0f3d] space-y-1.5">
                                <li>&#10038; Universities &amp; Colleges</li>
                                <li>&#10038; Community Colleges</li>
                                <li>&#10038; K&#8209;12 &amp; CTE Programs</li>
                                <li>&#10038; Workforce Development Agencies</li>
                            </ul>
                        </div>

                        <div>
                            <p className="font-sans text-xs uppercase tracking-[0.25em] text-[#6d91ad] mb-3">
                                Direct Email
                            </p>
                            <a
                                href="mailto:shana@theconnectionguru.co"
                                data-testid="contact-email-link"
                                className="font-serif text-xl text-[#0c0f3d] hover:text-[#e47a6a] transition-colors underline decoration-[#e3c7b9] underline-offset-4"
                            >
                                shana@theconnectionguru.co
                            </a>
                        </div>
                    </aside>

                    {/* FORM */}
                    <div className="lg:col-span-8">
                        <form onSubmit={submit} className="space-y-7" data-testid="contact-form">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                                <div>
                                    <label className="font-sans text-xs uppercase tracking-[0.25em] text-[#6d91ad] block mb-2">
                                        Full Name *
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={form.name}
                                        onChange={update("name")}
                                        placeholder="Dr. Maya Reynolds"
                                        className={inputClass}
                                        data-testid="contact-name-input"
                                    />
                                </div>
                                <div>
                                    <label className="font-sans text-xs uppercase tracking-[0.25em] text-[#6d91ad] block mb-2">
                                        Email *
                                    </label>
                                    <input
                                        type="email"
                                        required
                                        value={form.email}
                                        onChange={update("email")}
                                        placeholder="you@institution.edu"
                                        className={inputClass}
                                        data-testid="contact-email-input"
                                    />
                                </div>
                                <div>
                                    <label className="font-sans text-xs uppercase tracking-[0.25em] text-[#6d91ad] block mb-2">
                                        Organization
                                    </label>
                                    <input
                                        type="text"
                                        value={form.organization}
                                        onChange={update("organization")}
                                        placeholder="University, company, or board"
                                        className={inputClass}
                                        data-testid="contact-organization-input"
                                    />
                                </div>
                                <div>
                                    <label className="font-sans text-xs uppercase tracking-[0.25em] text-[#6d91ad] block mb-2">
                                        Your Role
                                    </label>
                                    <input
                                        type="text"
                                        value={form.role}
                                        onChange={update("role")}
                                        placeholder="Director, Dean, Chair..."
                                        className={inputClass}
                                        data-testid="contact-role-input"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="font-sans text-xs uppercase tracking-[0.25em] text-[#6d91ad] block mb-3">
                                    Inquiry Type *
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {inquiryTypes.map((t) => (
                                        <button
                                            type="button"
                                            key={t.value}
                                            onClick={() => setForm((f) => ({ ...f, inquiry_type: t.value }))}
                                            data-testid={`contact-type-${t.value}`}
                                            className={`px-4 py-2.5 text-xs uppercase tracking-[0.18em] border transition-colors ${
                                                form.inquiry_type === t.value
                                                    ? "bg-[#0c0f3d] text-[#fbf9f8] border-[#0c0f3d]"
                                                    : "bg-transparent text-[#0c0f3d] border-[#0c0f3d]/30 hover:border-[#0c0f3d]"
                                            }`}
                                        >
                                            {t.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="font-sans text-xs uppercase tracking-[0.25em] text-[#6d91ad] block mb-2">
                                    Message *
                                </label>
                                <textarea
                                    required
                                    rows={6}
                                    value={form.message}
                                    onChange={update("message")}
                                    placeholder="Tell us about your goals, audience, and timeline..."
                                    className={`${inputClass} resize-none`}
                                    data-testid="contact-message-input"
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="contact-submit-btn"
                                className="group inline-flex items-center gap-3 px-8 py-4 bg-[#0c0f3d] text-[#fbf9f8] text-xs uppercase tracking-[0.2em] hover:bg-[#e47a6a] transition-colors duration-300 disabled:opacity-50"
                            >
                                {loading ? "Sending..." : "Send Inquiry"}
                                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                            </button>
                        </form>
                    </div>
                </div>
            </section>
        </main>
    );
}
